import React, { createContext, useContext, useEffect, useState } from 'react'
import { auth } from '../../lib/supabase'
import { showError, showSuccess } from '../../shared/components/Toast'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Listen for auth state changes - this will handle initial session automatically
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.email)
      setUser(session?.user ?? null)
      setLoading(false)

      // Show success message for successful login
      if (event === 'SIGNED_IN' && session?.user) {
        showSuccess(`Welcome back, ${session.user.email}!`)
      }

      // Show message for sign out
      if (event === 'SIGNED_OUT') {
        showSuccess('You have been signed out successfully')
      }
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const { data, error } = await auth.signIn(email, password)
      
      if (error) {
        showError(error.message)
        return { success: false, error }
      }

      return { success: true, data }
    } catch (error) {
      console.error('Sign in error:', error)
      showError('An unexpected error occurred during sign in')
      return { success: false, error }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      const { error } = await auth.signOut()
      
      if (error) {
        showError(error.message)
        return { success: false, error }
      }

      return { success: true }
    } catch (error) {
      console.error('Sign out error:', error)
      showError('An unexpected error occurred during sign out')
      return { success: false, error }
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email) => {
    try {
      setLoading(true)
      const { data, error } = await auth.resetPassword(email)
      
      if (error) {
        showError(error.message)
        return { success: false, error }
      }

      showSuccess('Password reset email sent! Check your inbox.')
      return { success: true, data }
    } catch (error) {
      console.error('Reset password error:', error)
      showError('An unexpected error occurred while sending reset email')
      return { success: false, error }
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    loading,
    signIn,
    signOut,
    resetPassword,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
